/**
 * Account and workspace settings (CLAUDE.md 12.1; REVIEW-001 P3-5).
 *
 * The "Your data" section is not a compliance annex bolted on the side. Export and
 * deletion are ordinary account operations from the user's point of view -- see what
 * you have, take it with you, leave -- and framing them that way is how they end up
 * actually working rather than existing as a policy paragraph.
 *
 * Deletion tells the user what is blocking it *before* asking for a password, because
 * discovering "you own three shared projects" after typing your password is a small
 * cruelty that costs nothing to avoid.
 *
 * The `:section` parameter was a placeholder rendered into a hidden span. It is now
 * real: Members, Invitations and Usage are sub-pages, and **they appear only in an
 * organisation**. ADR-0002's promise is that a sole practitioner never meets the word
 * *workspace*, and a settings page offering them "Members" and "Invitations" for a
 * workspace of one breaks that promise more loudly than a switcher would.
 */
import { useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { Building2, Download, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError, api, tokens } from '@/api/client'
import { useWorkspace } from '@/api/workspaces'
import { useAuth } from '@/auth/AuthProvider'
import { WorkspaceChrome } from '@/components/layout/WorkspaceChrome'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { ThemeToggle } from '@/components/ui/ThemeToggle'
import { useToast } from '@/components/ui/toast'
import { WorkspaceSettings } from '@/components/layout/WorkspaceSettings'
import { LetterheadPanel } from '@/components/workspace/LetterheadPanel'
import { CreateOrganisationDialog } from '@/components/workspace/CreateOrganisationDialog'
import { DeleteWorkspaceCard } from '@/components/workspace/DeleteWorkspaceCard'
import { InvitationsPanel } from '@/components/workspace/InvitationsPanel'
import { MembersPanel } from '@/components/workspace/MembersPanel'
import { UsagePanel } from '@/components/workspace/UsagePanel'
import { cn } from '@/lib/cn'

/** Sub-pages of `/settings/:section`. All but `general` are organisation-only. */
const SECTIONS = ['general', 'members', 'invitations', 'usage'] as const
type SectionId = (typeof SECTIONS)[number]

const ORGANISATION_SECTIONS: SectionId[] = ['members', 'invitations', 'usage']

interface DeletionCheck {
  can_delete: boolean
  blocking_projects: { id: number; name: string }[]
}

export function SettingsPage() {
  const { t } = useTranslation(['common', 'auth'])
  const { section } = useParams()
  const { data: workspace } = useWorkspace()

  const organisation = workspace ? !workspace.is_personal : false
  // An unknown or organisation-only section falls back to General rather than to a
  // not-found page: the visitor followed a stale link or switched into a personal
  // workspace with `/settings/members` still in the address bar, and the useful
  // answer in both cases is the page they can actually see.
  const requested = SECTIONS.find((id) => id === section)
  const active: SectionId =
    requested && (organisation || !ORGANISATION_SECTIONS.includes(requested))
      ? requested
      : 'general'

  return (
    <WorkspaceChrome title={t('settings.title')}>
      <div className="mx-auto max-w-2xl space-y-4 p-4">
        <h2 className="text-lg font-semibold">{t('settings.title')}</h2>

        {organisation && <SettingsNav active={active} />}

        {active === 'general' && <GeneralSection />}
        {active === 'members' && <MembersPanel />}
        {active === 'invitations' && <InvitationsPanel />}
        {active === 'usage' && <UsagePanel />}
      </div>
    </WorkspaceChrome>
  )
}

/** Section links. Scrolls inside itself so the page body never scrolls sideways (14.3). */
function SettingsNav({ active }: { active: SectionId }) {
  const { t } = useTranslation()

  return (
    <nav className="-mx-1 overflow-x-auto" aria-label={t('settings.sections')}>
      <div className="flex w-max gap-1 px-1">
        {SECTIONS.map((id) => (
          <NavLink
            key={id}
            to={id === 'general' ? '/settings' : `/settings/${id}`}
            end={id === 'general'}
            aria-current={active === id ? 'page' : undefined}
            className={cn(
              'flex h-11 items-center whitespace-nowrap rounded-md px-3 text-sm',
              active === id ? 'bg-surface-raised text-text' : 'text-text-muted hover:text-text',
            )}
          >
            {t(`settings.nav.${id}`)}
          </NavLink>
        ))}
      </div>
    </nav>
  )
}

function GeneralSection() {
  const { t } = useTranslation(['common', 'auth'])
  const { user, workspaces, refreshUser, logout } = useAuth()
  const { toast } = useToast()

  // `null` means "untouched", so the field shows the saved value without copying it
  // into state -- which would otherwise need an effect to stay in sync, and would
  // silently discard a refetch while the user was typing.
  const [draftName, setDraftName] = useState<string | null>(null)
  const displayName = draftName ?? user?.display_name ?? ''

  const [savingProfile, setSavingProfile] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [check, setCheck] = useState<DeletionCheck | null>(null)

  const saveProfile = async () => {
    setSavingProfile(true)
    try {
      await api.patch('/auth/me', { display_name: displayName })
      await refreshUser()
      setDraftName(null)
      toast({ title: t('common.saved'), variant: 'success' })
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof ApiError ? error.localised : undefined,
        variant: 'error',
      })
    } finally {
      setSavingProfile(false)
    }
  }

  const exportData = async () => {
    // Authenticated download: the export is the most personal payload the API
    // produces, so it cannot be a plain link (15.3).
    const response = await fetch('/api/v1/me/export', {
      headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
    })
    if (!response.ok) {
      toast({ title: t('common.error'), variant: 'error' })
      return
    }
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `calcdesk-export-${user?.username ?? 'account'}.json`
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const openDeletion = async () => {
    try {
      setCheck(await api.get<DeletionCheck>('/me/deletion-check'))
      setDeleting(true)
    } catch {
      setDeleting(true)
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.account')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <Field>
            <FieldLabel>{t('settings.email')}</FieldLabel>
            <FieldInput value={user?.email ?? ''} readOnly disabled />
          </Field>
          <Field>
            <FieldLabel>{t('settings.username')}</FieldLabel>
            <FieldInput value={user?.username ?? ''} readOnly disabled />
          </Field>
          <Field>
            <FieldLabel>{t('settings.display_name')}</FieldLabel>
            <FieldInput
              value={displayName}
              onChange={(event) => setDraftName(event.target.value)}
            />
          </Field>
          <Button variant="primary" onClick={saveProfile} loading={savingProfile}>
            {t('action.save')}
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.appearance')}</CardTitle>
        </CardHeader>
        <CardBody className="flex items-center gap-4">
          {/* Both persist to the server, so the choice follows the engineer to
                another device (14.2, 14.4). */}
          <ThemeToggle />
          <LanguageToggle />
        </CardBody>
      </Card>

      <WorkspaceSettings />

      {/* Branding sits in its own card because the logo is a file: it saves on
          upload rather than with the rest of the form (12.1, 8.1). Shown in a
          personal workspace too -- a sole practitioner has a letterhead. */}
      <LetterheadPanel />

      {/* The switcher is hidden while there is only one workspace, which is right
            (ADR-0002) but leaves a solo engineer with no way to make a second. This
            is that door, and it appears exactly when the switcher does not (11.5). */}
      {workspaces.length <= 1 && <CreateOrganisationCard />}

      {/* Renders nothing in a personal workspace, or without `workspace:delete`. */}
      <DeleteWorkspaceCard />

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.privacy')}</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <p className="font-medium">{t('settings.export_title')}</p>
            <p className="mt-1 text-sm text-text-muted">{t('settings.export_hint')}</p>
            <Button className="mt-2" onClick={exportData}>
              <Download size={15} aria-hidden />
              {t('settings.export_action')}
            </Button>
          </div>

          <div className="border-t border-border pt-4">
            <p className="font-medium text-[var(--status-fail)]">{t('settings.delete_title')}</p>
            <p className="mt-1 text-sm text-text-muted">{t('settings.delete_hint')}</p>
            <Button variant="danger" className="mt-2" onClick={openDeletion}>
              <Trash2 size={15} aria-hidden />
              {t('settings.delete_action')}
            </Button>
          </div>
        </CardBody>
      </Card>

      <DeleteAccountDialog
        open={deleting}
        onOpenChange={setDeleting}
        check={check}
        onDeleted={async () => {
          await logout()
          window.location.href = '/login'
        }}
      />
    </>
  )
}

/** The one-sentence explanation, then the door. */
function CreateOrganisationCard() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('workspace.create_org')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-sm text-text-muted">{t('workspace.create_org_hint')}</p>
        <Button onClick={() => setOpen(true)}>
          <Building2 size={15} aria-hidden />
          {t('workspace.create_org')}
        </Button>
      </CardBody>
      <CreateOrganisationDialog open={open} onOpenChange={setOpen} />
    </Card>
  )
}

function DeleteAccountDialog({
  open,
  onOpenChange,
  check,
  onDeleted,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  check: DeletionCheck | null
  onDeleted: () => Promise<void>
}) {
  const { t } = useTranslation(['common', 'auth'])
  const { toast } = useToast()
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)

  const blocked = check ? !check.can_delete : false

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('settings.delete_title')}</DialogTitle>
          <DialogDescription>{t('settings.delete_hint')}</DialogDescription>
        </DialogHeader>

        {blocked ? (
          <div className="space-y-2">
            <p className="text-sm text-[var(--status-warn)]">{t('settings.delete_blocked')}</p>
            <ul className="list-inside list-disc text-sm text-text-muted">
              {check?.blocking_projects.map((project) => (
                <li key={project.id}>{project.name}</li>
              ))}
            </ul>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={async (event) => {
              event.preventDefault()
              setPending(true)
              try {
                await api.request('/me', {
                  method: 'DELETE',
                  body: { password },
                })
                await onDeleted()
              } catch (error) {
                toast({
                  title: t('common.error'),
                  description: error instanceof ApiError ? error.localised : undefined,
                  variant: 'error',
                })
              } finally {
                setPending(false)
              }
            }}
          >
            <Field>
              {/* Password-confirmed because it is irreversible: a live session proves
                  the browser is signed in, not that this person intends to leave. */}
              <FieldLabel>{t('settings.delete_confirm')}</FieldLabel>
              <FieldInput
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                {t('action.cancel')}
              </Button>
              <Button type="submit" variant="danger" loading={pending}>
                {t('settings.delete_action')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
